import { useEffect, useMemo, useState } from 'react';
import { CaretDown, ListBullets } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import { altitudeColor, formatAltitude } from '../map/altitude';
import { matchesFilters } from '../map/filters';

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export default function AircraftList() {
  const [open, setOpen] = useState(false);
  // The list itself stays mounted for a beat after closing so it can
  // transition out instead of disappearing instantly (and unmounts fully
  // once closed so a large aircraft list isn't rendered off-screen).
  const [mounted, setMounted] = useState(false);
  const aircraft = useStore((s) => s.aircraft);
  const area = useStore((s) => s.area);
  const select = useStore((s) => s.select);
  const selected = useStore((s) => s.selectedIcao24);
  const filters = useStore((s) => s.filters);

  const sorted = useMemo(() => {
    const list = [...aircraft.values()].filter((ac) => matchesFilters(ac, filters));
    if (!area) return list;
    const clat = (area.lamin + area.lamax) / 2;
    const clon = (area.lomin + area.lomax) / 2;
    return list
      .map((ac) => ({ ac, dist: distanceKm(clat, clon, ac.lat, ac.lon) }))
      .sort((a, b) => a.dist - b.dist)
      .map((x) => x.ac);
  }, [aircraft, area, filters]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (mounted) {
      const t = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={`aircraft-list${open ? ' open' : ''}`}>
      {mounted && (
        <div className="aircraft-list-panel glass">
          <div className="aircraft-list-head">
            <span>Nearest to area center</span>
            <span className="mono">{sorted.length}</span>
          </div>
          {sorted.length === 0 ? (
            <p className="aircraft-list-empty">
              Nothing matches right now. Loosen the filters or wait for the next update.
            </p>
          ) : (
            <ul>
              {sorted.map((ac) => (
                <li key={ac.icao24}>
                  <button
                    className={ac.icao24 === selected ? 'selected' : ''}
                    onClick={() => select(ac.icao24)}
                  >
                    <span
                      className="alt-swatch"
                      style={{ background: altitudeColor(ac.altitudeFt, ac.onGround) }}
                      aria-hidden="true"
                    />
                    <span className="cs mono">{ac.callsign ?? ac.icao24.toUpperCase()}</span>
                    <span className="alt mono">{formatAltitude(ac.altitudeFt, ac.onGround)}</span>
                    <span className="spd mono">{ac.groundSpeedKt != null ? `${ac.groundSpeedKt} kt` : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <button
        className={`chip list-toggle${open ? ' is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <ListBullets size={15} weight="bold" aria-hidden="true" />
        Aircraft
        <span className="chip-num mono">{sorted.length}</span>
        <span className="chip-caret" aria-hidden="true">
          <CaretDown size={12} weight="bold" />
        </span>
      </button>
    </div>
  );
}
